import type { ReactNode } from 'react';

import {
  RiFileCodeLine,
  RiFileSearchLine,
  RiFolderOpenLine,
  RiGitPullRequestLine,
  RiGitRepositoryLine,
  RiSearchLine,
} from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';

import {
  type ToolGroupEntry,
  ToolLabel,
  type ToolRenderOptions,
  ToolResultMessage,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';

import { confidenceChatPlugin } from '../plugin';
import { formatLineCount } from './formatLineCount';
import {
  getToolInput as getInput,
  getInputString as getString,
  truncateToolSubject as truncateSubject,
} from './parseToolResult';

type ToolInput = Record<string, unknown>;

type CodeExplorerConfig = {
  name: string;
  icon: RemixiconComponentType;
  streamingLabel: string;
  runningLabel: string;
  completedLabel: string;
  subject: (input: ToolInput) => string | undefined;
  renderSubject?: (input: ToolInput) => ReactNode;
  resultSummary: (content: string) => string;
};

const getRepository = (input: ToolInput): string | undefined => {
  const repository = getString(input, 'repository');
  return repository?.slice(repository.lastIndexOf('/') + 1) || undefined;
};

const getRepositories = (input: ToolInput): string | undefined => {
  const repositories = input.repositories;
  if (!Array.isArray(repositories) || repositories.length === 0)
    return undefined;
  if (repositories.length === 1 && typeof repositories[0] === 'string') {
    return repositories[0].slice(repositories[0].lastIndexOf('/') + 1);
  }
  return `${repositories.length} repositories`;
};

const joinSubject = (...parts: (string | undefined)[]): string | undefined =>
  parts.filter(Boolean).join(' ') || undefined;

const truncatePathStart = (path: string): string => {
  if (path.length <= 40) return path;
  const suffix = path.slice(-39);
  const nextSeparator = suffix.indexOf('/');
  return `…${nextSeparator >= 0 ? suffix.slice(nextSeparator) : suffix}`;
};

const getLineRange = (input: ToolInput): string | undefined => {
  const start = input.start_line;
  const end = input.end_line;
  const startLine =
    typeof start === 'number' && Number.isInteger(start) && start > 0
      ? start
      : undefined;
  const endLine =
    typeof end === 'number' && Number.isInteger(end) && end > 0
      ? end
      : undefined;
  if (startLine === undefined && endLine === undefined) return undefined;
  return `(${startLine ?? 1}-${endLine ?? 'end'})`;
};

const getFileLineCount = (content: string): string => {
  const bodyStart = content.indexOf('\n\n');
  return formatLineCount(
    bodyStart >= 0 ? content.slice(bodyStart + 2) : content,
  );
};

const getPullRequestSubject = (input: ToolInput): string | undefined => {
  const number = input.number;
  return joinSubject(
    getRepository(input),
    typeof number === 'number' && Number.isInteger(number) && number > 0
      ? `#${number}`
      : undefined,
  );
};

const getCount = (
  content: string,
  pattern: RegExp,
  singular: string,
  plural: string,
): string => {
  const count = content.match(pattern)?.[1];
  if (count === undefined) return formatLineCount(content);
  return `${count} ${count === '1' ? singular : plural}`;
};

const createCodeExplorerExtension = ({
  name,
  icon,
  streamingLabel,
  runningLabel,
  completedLabel,
  subject,
  renderSubject,
  resultSummary,
}: CodeExplorerConfig) =>
  confidenceChatPlugin.provide(
    createToolExtension({
      id: `code-explorer-${name.replaceAll('_', '-')}`,
      predicate: tool => tool === `mcp__code_explorer__${name}`,
      icon,
      display: 'grouped',
      collapseConsecutiveCalls: false,
      render: () => null,
      renderLabel: (entry: ToolGroupEntry, options: ToolRenderOptions) => {
        const input = getInput(entry.input);
        const subjectText = subject(input);
        let action = runningLabel;
        if (options.isStreaming) action = streamingLabel;
        else if (entry.result?.success) action = completedLabel;

        let summary: string | undefined;
        if (entry.result) {
          summary = entry.result.success
            ? resultSummary(entry.result.content)
            : 'Failed';
        }

        return (
          <ToolLabel.Root>
            <ToolLabel.Primary>
              {action}
              {!options.isStreaming && subjectText && (
                <>
                  {' '}
                  <span title={renderSubject ? undefined : subjectText}>
                    {renderSubject
                      ? renderSubject(input)
                      : truncateSubject(subjectText)}
                  </span>
                </>
              )}
            </ToolLabel.Primary>
            {summary && <ToolLabel.Secondary>{summary}</ToolLabel.Secondary>}
            <ToolLabel.Timer
              startedAt={entry.result ? undefined : entry.startedAt}
            />
            {entry.result && (
              <ToolLabel.Details>
                <ToolResultMessage
                  content={entry.result.content}
                  success={entry.result.success}
                />
              </ToolLabel.Details>
            )}
          </ToolLabel.Root>
        );
      },
    }),
  );

export const ListRepositoriesToolExtension = createCodeExplorerExtension({
  name: 'list_repositories',
  icon: RiGitRepositoryLine,
  streamingLabel: 'Listing repositories',
  runningLabel: 'Listing repositories',
  completedLabel: 'Listed repositories',
  subject: () => undefined,
  resultSummary: content =>
    getCount(
      content,
      /^Found (\d+) repositories:/,
      'repository',
      'repositories',
    ),
});

export const ReadFileToolExtension = createCodeExplorerExtension({
  name: 'read_file',
  icon: RiFileCodeLine,
  streamingLabel: 'Reading file',
  runningLabel: 'Reading',
  completedLabel: 'Read',
  subject: input => {
    const repository = getRepository(input);
    return joinSubject(
      getString(input, 'path') ?? 'file',
      getLineRange(input),
      repository ? `in ${repository}` : undefined,
    );
  },
  renderSubject: input => {
    const path = getString(input, 'path');
    const lineRange = getLineRange(input);
    const repository = getRepository(input);
    return (
      <>
        {path ? <span title={path}>{truncatePathStart(path)}</span> : 'file'}
        {lineRange && <> {lineRange}</>}
        {repository && <> in {repository}</>}
      </>
    );
  },
  resultSummary: getFileLineCount,
});

export const SearchCodeToolExtension = createCodeExplorerExtension({
  name: 'search_code',
  icon: RiSearchLine,
  streamingLabel: 'Searching',
  runningLabel: 'Searching for',
  completedLabel: 'Searched for',
  subject: input => {
    const query = getString(input, 'query');
    if (!query) return undefined;
    const repository = getRepositories(input);
    return `${query} ${repository ? `in ${repository}` : 'across repositories'}`;
  },
  renderSubject: input => {
    const query = getString(input, 'query');
    if (!query) return null;
    const repository = getRepositories(input);
    return (
      <>
        <strong title={query}>{truncateSubject(query)}</strong>{' '}
        {repository
          ? `in ${truncateSubject(repository)}`
          : 'across repositories'}
      </>
    );
  },
  resultSummary: content =>
    content.startsWith('No results found for:')
      ? '0 results'
      : getCount(content, /^Found (\d+) results:/, 'result', 'results'),
});

export const GlobFilesToolExtension = createCodeExplorerExtension({
  name: 'glob_files',
  icon: RiFolderOpenLine,
  streamingLabel: 'Finding files',
  runningLabel: 'Finding files',
  completedLabel: 'Found files',
  subject: input =>
    joinSubject(getRepository(input), getString(input, 'pattern')),
  resultSummary: content =>
    content.startsWith('No files matched pattern:')
      ? '0 files'
      : getCount(content, /^Found (\d+) files matching /, 'file', 'files'),
});

export const ListPullRequestsToolExtension = createCodeExplorerExtension({
  name: 'list_pull_requests',
  icon: RiGitPullRequestLine,
  streamingLabel: 'Listing pull requests',
  runningLabel: 'Listing pull requests',
  completedLabel: 'Listed pull requests',
  subject: input =>
    joinSubject(getRepository(input), getString(input, 'filter')),
  resultSummary: content =>
    content.startsWith('No pull requests found')
      ? '0 pull requests'
      : getCount(
          content,
          /^Found (\d+) pull requests/,
          'pull request',
          'pull requests',
        ),
});

export const GetPullRequestToolExtension = createCodeExplorerExtension({
  name: 'get_pull_request',
  icon: RiGitPullRequestLine,
  streamingLabel: 'Reading pull request',
  runningLabel: 'Reading pull request',
  completedLabel: 'Read pull request',
  subject: getPullRequestSubject,
  resultSummary: content =>
    content.match(/^.*PR #\d+: (.*) \[[^\]]+\]/)?.[1] ??
    formatLineCount(content),
});

export const GetPullRequestDiffToolExtension = createCodeExplorerExtension({
  name: 'get_pull_request_diff',
  icon: RiFileSearchLine,
  streamingLabel: 'Reading pull request diff',
  runningLabel: 'Reading pull request diff',
  completedLabel: 'Read pull request diff',
  subject: getPullRequestSubject,
  resultSummary: getFileLineCount,
});

export const SearchPullRequestsToolExtension = createCodeExplorerExtension({
  name: 'search_pull_requests',
  icon: RiGitPullRequestLine,
  streamingLabel: 'Searching pull requests',
  runningLabel: 'Searching pull requests',
  completedLabel: 'Searched pull requests',
  subject: input =>
    joinSubject(
      getString(input, 'query'),
      getString(input, 'filter'),
      getRepositories(input),
    ),
  resultSummary: content =>
    content.startsWith('No pull requests found')
      ? '0 pull requests'
      : getCount(
          content,
          /^Found (\d+) pull requests/,
          'pull request',
          'pull requests',
        ),
});
